import { COLORS } from '@consts/general';
import { BaseReport } from '../BaseReport';
import { useReportLoad } from '@hooks/useReportLoad';
import { average, count, sum } from '@utils/summaries';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { getUniqueDates, groupBy } from '@utils/data';
import { styleDate, styleDateRange } from '@utils/styles';
import { chart } from '@utils/charts';
import { REPORT } from '@consts/reports';

interface TotalSuccessfulCutsByMonthProps {
    dateRange: { startDate: string; endDate: string };
}

export const TotalSuccessfulCutsByMonth = ({ dateRange }: TotalSuccessfulCutsByMonthProps) => {
    const [range, setRange] = useRange('TotalSuccessfulCutsByMonth');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.TOTAL_SUCCESSFUL_CUTS_BY_MONTH,
        params: { range, ...dateRange },
    });

    const dateKeys = getUniqueDates(data, range);

    const groupedByDate = groupBy(data, 'date');

    const successCutsSeries = {
        data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.successfulCuts || 0),
        type: 'column',
        name: 'Successful Cuts',
        color: COLORS.BLUE,
        yAxis: 0,
        dataLabels: {
            enabled: true,
            color: 'black',
        },
    };

    const percentChangeSeries = {
        data: dateKeys.map(({ date }) => groupedByDate[date]?.[0]?.percentageChange || 0),
        type: 'line',
        name: '% Change',
        color: COLORS.YELLOW_DARK,
        yAxis: 1,
        dataLabels: {
            enabled: true,
        },
        marker: {
            fillColor: COLORS.RED,
            radius: 3,
        },
    };

    return (
        <BaseReport
            title={'Total Successful Cuts by Month'}
            description={
                'This graph shows your total cut volume (successful, new sale cuts only) by month, for the date range selected above.'
            }
            loading={loading}
            hasData={hasData}
            setRange={setRange}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            range={range}
            tableOptions={{
                headers: ['Date', 'Successful Cuts', '% Change'],
                data: data.map((item) => [item.date, item.successfulCuts, item.percentageChange]),
                summaries: [count, sum, average],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort],
            }}
            chartOptions={chart(
                300,
                [
                    { name: 'Successful Cuts', opposite: false },
                    { name: '% Change', opposite: true },
                ],
                dateKeys,
                [successCutsSeries, percentChangeSeries],
                undefined,
                {
                    legend: { align: 'right', verticalAlign: 'top' },
                }
            )}
        />
    );
};
